import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue';
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createVNode as _createVNode, isRef as _isRef, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "w-full flex flex-col items-center gap-0.5 text-center" };
const _hoisted_2 = ["id"];
const _hoisted_3 = ["id"];
const _hoisted_4 = { class: "text-sm font-normal text-(--colors-text-body-subtle)" };
const _hoisted_5 = { class: "flex flex-col gap-5" };
const _hoisted_6 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col gap-4" };
const _hoisted_7 = { class: "font-semibold text-(--colors-text-heading)" };
const _hoisted_8 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col gap-4" };
const _hoisted_9 = { class: "font-semibold text-(--colors-text-heading)" };
const _hoisted_10 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_11 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col gap-4" };
const _hoisted_12 = { class: "font-semibold text-(--colors-text-heading)" };
const _hoisted_13 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
const _hoisted_14 = { class: "border border-(--colors-border-base-soft) rounded-xl p-5 flex flex-col gap-4" };
const _hoisted_15 = { class: "font-semibold text-(--colors-text-heading)" };
const _hoisted_16 = { class: "grid grid-cols-1 sm:grid-cols-2 gap-4" };
import { computed, reactive, ref, toRef, useId, watch } from "vue";
import { useI18n } from "vue-i18n";
import { isNavigationFailure, NavigationFailureType, useRouter } from "vue-router";
import { Button, DatePickerInput, Modal, SelectInput, Textarea, TextInput } from "@eudonet/opal";
import { PROJECT_STATUSES } from "../../domain/entities/Project";
import { createProject } from "../../infrastructure/api/projectsApi";
import { useAppToast } from "@/shared/composables/useAppToast";
import { resolveApiErrorKey, getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { useDateIsoBinding } from "@/shared/composables/forms/useDateIsoBinding";
import { toWireDate } from "../composables/wireDate";
import { toDatePickerFormat } from "@/shared/utils/date-formatting";
import { useProfileStore } from "@/modules/settings/public";
import { useFinanceFormatters } from "@/shared/composables/useFinanceFormatters";
import { logger } from "@/infrastructure/services/LoggerService";
export default /*@__PURE__*/ _defineComponent({
    __name: 'ProjectCreateModal',
    props: {
        "modelValue": { type: Boolean, ...{ default: false } },
        "modelModifiers": {},
    },
    emits: /*@__PURE__*/ _mergeModels(["created"], ["update:modelValue"]),
    setup(__props, { emit: __emit }) {
        const isOpen = _useModel(__props, "modelValue");
        const emit = __emit;
        const { t, locale } = useI18n();
        const router = useRouter();
        const { toastSuccess, toastError } = useAppToast();
        const profileStore = useProfileStore();
        const { parseAmount } = useFinanceFormatters();
        const titleId = useId();
        const descriptionId = useId();
        const submitting = ref(false);
        const form = reactive({
            name: "",
            actionDomain: "",
            description: "",
            status: "Idea",
            startDate: "",
            endDate: "",
            code: "",
            fundingSource: "",
            chargeAccount: "",
            analyticalCode: "",
        });
        const objectiveAmountInput = ref("");
        const errors = reactive({
            name: "",
            actionDomain: "",
            code: "",
            objectiveAmount: "",
        });
        const dateFormat = computed(() => toDatePickerFormat(profileStore.profile?.dateFormat, locale.value));
        const startDateDisplay = useDateIsoBinding(toRef(form, "startDate"), computed(() => profileStore.profile?.dateFormat), locale);
        const endDateDisplay = useDateIsoBinding(toRef(form, "endDate"), computed(() => profileStore.profile?.dateFormat), locale);
        const statusOptions = computed(() => PROJECT_STATUSES.map((status) => ({
            value: status,
            label: t(`projects.status.${status}`),
        })));
        // ISO-string comparison (the form stores ISO; the pickers speak display format through the
        // binding), so the rule holds whatever the user's profile date format is.
        const endDateError = computed(() => form.startDate && form.endDate && form.endDate < form.startDate
            ? t("projects.create.errors.endDateBeforeStart")
            : "");
        const parsedObjectiveAmount = computed(() => {
            const raw = objectiveAmountInput.value.trim();
            if (!raw)
                return null;
            const parsed = parseAmount(raw);
            return Number.isFinite(parsed) ? parsed : Number.NaN;
        });
        // The code-conflict error comes from the server, so no blur validator ever recomputes it —
        // without this watch a 409 would leave isFormValid false for the rest of the modal's life.
        watch(() => form.code, () => {
            errors.code = "";
        });
        function validateName() {
            errors.name = form.name.trim() ? "" : t("projects.create.errors.nameRequired");
        }
        function validateActionDomain() {
            errors.actionDomain = form.actionDomain.trim()
                ? ""
                : t("projects.create.errors.actionDomainRequired");
        }
        function validateObjectiveAmount() {
            const value = parsedObjectiveAmount.value;
            errors.objectiveAmount =
                value !== null && (Number.isNaN(value) || value < 0)
                    ? t("projects.create.errors.objectiveAmountInvalid")
                    : "";
        }
        const isFormValid = computed(() => !!form.name.trim() &&
            !!form.actionDomain.trim() &&
            !!form.status &&
            !endDateError.value &&
            !errors.name &&
            !errors.actionDomain &&
            !errors.code &&
            !errors.objectiveAmount);
        function resetForm() {
            form.name = "";
            form.actionDomain = "";
            form.description = "";
            form.status = "Idea";
            form.startDate = "";
            form.endDate = "";
            form.code = "";
            form.fundingSource = "";
            form.chargeAccount = "";
            form.analyticalCode = "";
            objectiveAmountInput.value = "";
            errors.name = "";
            errors.actionDomain = "";
            errors.code = "";
            errors.objectiveAmount = "";
            createdProjectId.value = null;
            submitting.value = false;
        }
        function handleClose() {
            if (submitting.value)
                return;
            resetForm();
            isOpen.value = false;
        }
        const CREATE_ERROR_KEYS = {
            "Project.Name.Required": "projects.create.errors.nameRequired",
            "Project.Name.TooLong": "projects.create.errors.nameTooLong",
            "Project.ActionDomain.Required": "projects.create.errors.actionDomainRequired",
            "Project.ActionDomain.TooLong": "projects.create.errors.actionDomainTooLong",
            "Project.Code.TooLong": "projects.create.errors.codeTooLong",
            "Project.Code.AlreadyExists": "projects.create.errors.codeTaken",
            "Project.Status.Required": "projects.create.errors.statusInvalid",
            "Project.Status.Invalid": "projects.create.errors.statusInvalid",
            "Project.Description.TooLong": "projects.create.errors.descriptionTooLong",
            "Project.ObjectiveAmount.Invalid": "projects.create.errors.objectiveAmountInvalid",
            "Project.EndDate.BeforeStartDate": "projects.create.errors.endDateBeforeStart",
            "Project.FundingSource.TooLong": "projects.create.errors.fundingSourceTooLong",
            "Project.ChargeAccount.TooLong": "projects.create.errors.chargeAccountTooLong",
            "Project.AnalyticalCode.TooLong": "projects.create.errors.analyticalCodeTooLong",
        };
        // Set once the POST succeeds. A refused/throwing navigation keeps the modal open with submit
        // enabled — the next click must RETRY the navigation, never re-POST an already-created project.
        const createdProjectId = ref(null);
        async function handleSubmit() {
            validateName();
            validateActionDomain();
            validateObjectiveAmount();
            if (!isFormValid.value)
                return;
            submitting.value = true;
            try {
                if (!createdProjectId.value) {
                    let created;
                    try {
                        created = await createProject({
                            name: form.name.trim(),
                            code: form.code.trim() || null,
                            status: form.status,
                            isDefault: false,
                            objectiveAmount: parsedObjectiveAmount.value,
                            startDate: toWireDate(form.startDate),
                            endDate: toWireDate(form.endDate),
                            description: form.description.trim() || null,
                            actionDomain: form.actionDomain.trim(),
                            fundingSource: form.fundingSource.trim() || null,
                            chargeAccount: form.chargeAccount.trim() || null,
                            analyticalCode: form.analyticalCode.trim() || null,
                        });
                    }
                    catch (err) {
                        const errorCode = getApiErrorCode(err);
                        const key = resolveApiErrorKey(err, CREATE_ERROR_KEYS, "projects.create.errorMessage");
                        // A code collision is fixable inline only when the user actually typed one — a blank
                        // code means the server-generated candidate collided, so the field has nothing to fix.
                        // Everything else is a toast (R46/R47).
                        if (errorCode === "Project.Code.AlreadyExists" && form.code.trim()) {
                            errors.code = t(key);
                        }
                        else {
                            toastError(key);
                        }
                        return;
                    }
                    createdProjectId.value = created.projectId;
                    toastSuccess("projects.create.successMessage");
                    emit("created", created.projectId);
                }
                let refused;
                try {
                    refused = await router.push({
                        name: "ProjectDetail",
                        params: { projectId: createdProjectId.value },
                        query: { tab: "details" },
                    });
                }
                catch (navError) {
                    logger.error("Navigation to the created project's record failed", navError);
                    return;
                }
                // A duplicated failure means the target fiche is already on screen — a success for this
                // flow, so the modal still closes. Only a guard refusal (aborted/cancelled) keeps it open.
                if (refused && !isNavigationFailure(refused, NavigationFailureType.duplicated))
                    return;
                resetForm();
                isOpen.value = false;
            }
            finally {
                submitting.value = false;
            }
        }
        return (_ctx, _cache) => {
            return (_openBlock(), _createBlock(_unref(Modal), {
                modelValue: isOpen.value,
                "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event) => ((isOpen).value = $event)),
                size: "lg",
                "footer-position": "split",
                "header-position": "center",
                "show-header-border": false,
                "show-footer-border": false,
                dismissable: !submitting.value,
                "close-on-escape": !submitting.value,
                "show-close-button": !submitting.value,
                "aria-labelledby": _unref(titleId),
                "aria-describedby": _unref(descriptionId),
                onClose: handleClose
            }, {
                header: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                        _createElementVNode("h3", {
                            id: _unref(titleId),
                            class: "text-xl font-bold text-(--colors-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.create.title")), 9 /* TEXT, PROPS */, _hoisted_2),
                        _createElementVNode("p", {
                            id: _unref(descriptionId),
                            class: "text-sm font-normal leading-5 text-(--text-text-heading)"
                        }, _toDisplayString(_unref(t)("projects.create.subtitle")), 9 /* TEXT, PROPS */, _hoisted_3),
                        _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)("projects.create.requiredFieldsHint")), 1 /* TEXT */)
                    ])
                ]),
                footer: _withCtx(() => [
                    _createVNode(_unref(Button), {
                        variant: "secondary",
                        label: _unref(t)('common.cancel'),
                        disabled: submitting.value,
                        "data-testid": "project-create-cancel",
                        onClick: handleClose
                    }, null, 8 /* PROPS */, ["label", "disabled"]),
                    _createVNode(_unref(Button), {
                        variant: "primary",
                        label: _unref(t)('projects.create.submit'),
                        disabled: !isFormValid.value || submitting.value,
                        loading: submitting.value,
                        "data-testid": "project-create-submit",
                        onClick: handleSubmit
                    }, null, 8 /* PROPS */, ["label", "disabled", "loading"])
                ]),
                default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("div", _hoisted_6, [
                            _createElementVNode("h4", _hoisted_7, _toDisplayString(_unref(t)("projects.create.sections.identification")), 1 /* TEXT */),
                            _createVNode(_unref(TextInput), {
                                modelValue: form.name,
                                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => ((form.name) = $event)),
                                label: _unref(t)('projects.create.nameLabel'),
                                required: "",
                                placeholder: _unref(t)('projects.create.namePlaceholder'),
                                maxlength: 200,
                                error: errors.name,
                                "full-width": "",
                                "data-testid": "project-create-name",
                                onBlur: validateName
                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                            _createVNode(_unref(TextInput), {
                                modelValue: form.actionDomain,
                                "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event) => ((form.actionDomain) = $event)),
                                label: _unref(t)('projects.create.actionDomainLabel'),
                                required: "",
                                placeholder: _unref(t)('projects.create.actionDomainPlaceholder'),
                                maxlength: 100,
                                error: errors.actionDomain,
                                "full-width": "",
                                "data-testid": "project-create-action-domain",
                                onBlur: validateActionDomain
                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                            _createVNode(_unref(Textarea), {
                                modelValue: form.description,
                                "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event) => ((form.description) = $event)),
                                label: _unref(t)('projects.create.descriptionLabel'),
                                placeholder: _unref(t)('projects.create.descriptionPlaceholder'),
                                rows: 3,
                                height: "auto",
                                "full-width": "",
                                "data-testid": "project-create-description"
                            }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder"])
                        ]),
                        _createElementVNode("div", _hoisted_8, [
                            _createElementVNode("h4", _hoisted_9, _toDisplayString(_unref(t)("projects.create.sections.schedule")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_10, [
                                _createVNode(_unref(SelectInput), {
                                    modelValue: form.status,
                                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((form.status) = $event)),
                                    label: _unref(t)('projects.create.statusLabel'),
                                    required: "",
                                    options: statusOptions.value,
                                    "full-width": "",
                                    "data-testid": "project-create-status"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "options"]),
                                _cache[12] || (_cache[12] = _createElementVNode("div", {
                                    class: "hidden sm:block",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createVNode(_unref(DatePickerInput), {
                                    modelValue: _unref(startDateDisplay),
                                    "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event) => (_isRef(startDateDisplay) ? (startDateDisplay).value = $event : null)),
                                    label: _unref(t)('projects.create.startDateLabel'),
                                    format: dateFormat.value,
                                    language: _unref(locale),
                                    "week-start": 1,
                                    "full-width": "",
                                    "data-testid": "project-create-start-date"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language"]),
                                _createVNode(_unref(DatePickerInput), {
                                    modelValue: _unref(endDateDisplay),
                                    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event) => (_isRef(endDateDisplay) ? (endDateDisplay).value = $event : null)),
                                    label: _unref(t)('projects.create.endDateLabel'),
                                    format: dateFormat.value,
                                    language: _unref(locale),
                                    "week-start": 1,
                                    error: endDateError.value,
                                    "full-width": "",
                                    "data-testid": "project-create-end-date"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "format", "language", "error"])
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("h4", _hoisted_12, _toDisplayString(_unref(t)("projects.create.sections.funding")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_13, [
                                _createVNode(_unref(TextInput), {
                                    modelValue: objectiveAmountInput.value,
                                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event) => ((objectiveAmountInput).value = $event)),
                                    label: _unref(t)('projects.create.objectiveAmountLabel'),
                                    placeholder: _unref(t)('projects.create.objectiveAmountPlaceholder'),
                                    inputmode: "decimal",
                                    error: errors.objectiveAmount,
                                    "full-width": "",
                                    "data-testid": "project-create-objective-amount",
                                    onBlur: validateObjectiveAmount
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.fundingSource,
                                    "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event) => ((form.fundingSource) = $event)),
                                    label: _unref(t)('projects.create.fundingSourceLabel'),
                                    maxlength: 200,
                                    "full-width": "",
                                    "data-testid": "project-create-funding-source"
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ])
                        ]),
                        _createElementVNode("div", _hoisted_14, [
                            _createElementVNode("h4", _hoisted_15, _toDisplayString(_unref(t)("projects.create.sections.accounting")), 1 /* TEXT */),
                            _createElementVNode("div", _hoisted_16, [
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.code,
                                    "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event) => ((form.code) = $event)),
                                    label: _unref(t)('projects.create.codeLabel'),
                                    placeholder: _unref(t)('projects.create.codePlaceholder'),
                                    maxlength: 50,
                                    error: errors.code,
                                    "full-width": "",
                                    "data-testid": "project-create-code"
                                }, null, 8 /* PROPS */, ["modelValue", "label", "placeholder", "error"]),
                                _cache[13] || (_cache[13] = _createElementVNode("div", {
                                    class: "hidden sm:block",
                                    "aria-hidden": "true"
                                }, null, -1 /* CACHED */)),
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.chargeAccount,
                                    "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event) => ((form.chargeAccount) = $event)),
                                    label: _unref(t)('projects.create.chargeAccountLabel'),
                                    maxlength: 50,
                                    "full-width": "",
                                    "data-testid": "project-create-charge-account"
                                }, null, 8 /* PROPS */, ["modelValue", "label"]),
                                _createVNode(_unref(TextInput), {
                                    modelValue: form.analyticalCode,
                                    "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event) => ((form.analyticalCode) = $event)),
                                    label: _unref(t)('projects.create.analyticalCodeLabel'),
                                    maxlength: 50,
                                    "full-width": "",
                                    "data-testid": "project-create-analytical-code"
                                }, null, 8 /* PROPS */, ["modelValue", "label"])
                            ])
                        ])
                    ])
                ]),
                _: 1 /* STABLE */
            }, 8 /* PROPS */, ["modelValue", "dismissable", "close-on-escape", "show-close-button", "aria-labelledby", "aria-describedby"]));
        };
    }
});
